import { useEffect, useRef } from 'react'

import { useUnsavedIntents } from '@/shared/routing/unsavedIntents'

/**
 * Reloads a stale tab after a redeploy, so an open page converges to the
 * current build without a forced refresh.
 *
 * The entry document is served `no-store` (deploy/compose/web/nginx.conf), so a
 * NAVIGATION always gets the new build. The tab this cannot reach is the one
 * already open across the rebuild — kept on another monitor, restored by the
 * browser's session restore, or resumed from the back/forward cache. Its SPA
 * keeps running whatever it booted with, and every in-app route change is
 * client-side, so nothing ever refetches the document. That tab shows the old
 * interface until someone force-reloads it by hand.
 *
 * The watcher closes that gap from inside: while the tab remains visible and
 * whenever it regains the user's attention, it refetches the entry document
 * and compares its build identity with the one this tab booted from. The
 * identity is every content-hashed `/assets/` name the document references —
 * script AND stylesheet, because a CSS-only deploy renames only the stylesheet
 * — so a difference means a newer build is deployed, and the watcher reloads
 * the page onto it.
 *
 * It reloads ONLY while the unsaved-intent registry is empty. A reload with an
 * open intent would discard clinical text the server has not accepted, which is
 * strictly worse than showing a stale interface. A held-back tab remembers the
 * newer build and retries at every scheduled or attention check — outside the
 * quiet gap, because the question left is not "is there an update" but "may I
 * act on the one I found" — so saving the work makes the next check reload.
 */

/**
 * Every content-hashed asset name the document ties itself to, as one
 * order-independent string. Null when the document carries no hashed module
 * entry: the dev server serves `/src/main.tsx` instead and a jsdom test
 * document carries none, which is what disarms the watcher outside the built
 * product.
 */
export function buildIdentity(doc: Document): string | null {
  if (doc.querySelector('script[type="module"][src*="/assets/"]') === null) {
    return null
  }
  const references = [
    ...doc.querySelectorAll('script[type="module"][src*="/assets/"]'),
    ...doc.querySelectorAll('link[rel="stylesheet"][href*="/assets/"]'),
  ].map((element) => element.getAttribute('src') ?? element.getAttribute('href') ?? '')
  return references.sort().join('\n')
}

export function buildIdentityOfHtml(html: string): string | null {
  return buildIdentity(new DOMParser().parseFromString(html, 'text/html'))
}

async function fetchIndexHtml(): Promise<string | null> {
  try {
    // `no-store` on the request too: the comparison is only meaningful against
    // what the server would give a navigation right now, never against a copy.
    const response = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' })
    return response.ok ? await response.text() : null
  } catch {
    // Unreachable mid-rebuild or offline: stay quiet and retry on the next
    // attention gain rather than surfacing a transient transport error.
    return null
  }
}

function reloadWindow(): void {
  window.location.reload()
}

/**
 * Attention events arrive in bursts (focus and visibilitychange fire together),
 * so checks are spaced out. A monotonic clock, not `Date.now`: the wall clock
 * on the machines this runs on can step, and a backwards step would silence
 * the watcher for as long as the step was large.
 */
const CHECK_GAP_MS = 60_000

export function BuildUpdateWatcher({
  fetchDocument = fetchIndexHtml,
  reload = reloadWindow,
}: {
  /** Tests substitute the transport and the reload; the product renders bare. */
  fetchDocument?: () => Promise<string | null>
  reload?: () => void
} = {}) {
  const unsaved = useUnsavedIntents()
  const holdingRef = useRef(false)
  holdingRef.current = unsaved.length > 0

  useEffect(() => {
    const booted = buildIdentity(document)
    if (booted === null) return

    let disposed = false
    let inFlight = false
    let updateHeld = false
    let lastCheckAt = Number.NEGATIVE_INFINITY

    const check = () => {
      if (document.visibilityState !== 'visible') return
      if (updateHeld) {
        // The newer build is already known; only the hold is in question. No
        // refetch and no quiet gap, or saving inside the gap could not make
        // the next return reload.
        if (!holdingRef.current) reload()
        return
      }
      const now = performance.now()
      if (inFlight || now - lastCheckAt < CHECK_GAP_MS) return
      inFlight = true
      lastCheckAt = now
      void fetchDocument()
        .then((html) => {
          if (disposed || html === null) return
          const latest = buildIdentityOfHtml(html)
          if (latest !== null && latest !== booted) {
            if (holdingRef.current) {
              updateHeld = true
            } else {
              reload()
            }
          }
        })
        .finally(() => {
          inFlight = false
        })
    }

    document.addEventListener('visibilitychange', check)
    window.addEventListener('focus', check)
    // A back/forward-cache restore resumes the old page without a navigation;
    // `pageshow` is the event that restore is guaranteed to fire.
    window.addEventListener('pageshow', check)
    // A clinician may keep this page continuously focused through a deploy.
    // Attention events cannot detect that case, so visible tabs also converge
    // on the same bounded cadence. Hidden tabs return early and wait for their
    // ordinary visibility event instead of producing background traffic.
    const intervalId = window.setInterval(check, CHECK_GAP_MS)
    return () => {
      disposed = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('focus', check)
      window.removeEventListener('pageshow', check)
    }
  }, [fetchDocument, reload])

  return null
}
